import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Shield, Download, RefreshCw, Mail, Phone, Globe, Calendar, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import axios from "axios";

const TOKEN_KEY = "wprescate24_admin_token";

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [authError, setAuthError] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const loadLeads = async (authToken = token) => {
    if (!backendUrl || !authToken) return;
    setLoading(true);
    setAuthError(false);
    try {
      const response = await axios.get(`${backendUrl}/api/contact`, {
        headers: { "X-Admin-Token": authToken },
      });
      setLeads(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        setAuthError(true);
        sessionStorage.removeItem(TOKEN_KEY);
      }
      console.error("Error cargando leads del servidor:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadLeads(token);
  }, []);

  const submitToken = (e) => {
    e.preventDefault();
    sessionStorage.setItem(TOKEN_KEY, token);
    loadLeads(token);
  };

  const exportToCSV = () => {
    const headers = ["Fecha", "Nombre", "Email", "Teléfono", "URL", "Plan", "Urgencia", "Canal", "Mensaje", "Fuente"];
    const csv = [
      headers.join(","),
      ...leads.map(lead => [
        new Date(lead.createdAt).toLocaleString(),
        lead.nombre,
        lead.email,
        lead.telefono,
        lead.url,
        lead.plan,
        lead.urgencia,
        lead.canal,
        `"${(lead.mensaje || "").replace(/"/g, '""')}"`,
        lead.source || "N/A"
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-wprescate24-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const leadsHoy = leads.filter(l => {
    const leadDate = new Date(l.createdAt).toDateString();
    return leadDate === new Date().toDateString();
  }).length;

  if (!backendUrl) {
    return (
      <div className="dark min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center space-y-3">
            <Lock className="w-8 h-8 mx-auto text-foreground/50" />
            <p className="text-foreground">
              El panel de administración requiere el backend configurado
              (<code>REACT_APP_BACKEND_URL</code>). En el despliegue estático los leads
              se entregan por correo vía lead-relay.
            </p>
            <RouterLink to="/" className="text-primary hover:underline inline-block">Volver al inicio</RouterLink>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-sm bg-white/95 border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <RouterLink to="/" className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <span className="font-bold text-foreground text-lg">WPRescate24 Admin</span>
          </RouterLink>
          <div className="flex items-center gap-3">
            <Button onClick={() => loadLeads()} variant="outline" size="sm" disabled={!token}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button onClick={exportToCSV} className="bg-primary hover:bg-primary/90" disabled={!token || leads.length === 0}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {!token ? (
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="text-foreground text-lg flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" /> Acceso restringido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitToken} className="space-y-4">
                <Input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Token de administrador"
                  required
                />
                {authError && (
                  <p className="text-sm text-destructive">Token inválido. Intenta de nuevo.</p>
                )}
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90">Entrar</Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-foreground/70">Total Leads</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-primary">{leads.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-foreground/70">Hoy</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-foreground">{leadsHoy}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-foreground/70">Sesión</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      sessionStorage.removeItem(TOKEN_KEY);
                      setToken("");
                      setLeads([]);
                    }}
                  >
                    Cerrar sesión
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Leads Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-foreground text-xl">Registros de Contacto</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-foreground/70 text-center py-10">Cargando registros...</p>
                ) : leads.length === 0 ? (
                  <p className="text-foreground/70 text-center py-10">
                    {authError ? "Token inválido." : "No hay registros todavía"}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {leads.map((lead, idx) => (
                      <Card key={lead.id || idx} className="bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-border">
                        <CardContent className="p-6">
                          <div className="grid md:grid-cols-2 gap-4">
                            {/* Left Column */}
                            <div className="space-y-3">
                              <div>
                                <h3 className="text-lg font-bold text-foreground mb-1">{lead.nombre}</h3>
                                <div className="flex items-center gap-2 text-sm text-foreground/70">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(lead.createdAt).toLocaleString('es-MX')}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="w-4 h-4 text-primary" />
                                <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                                  {lead.email}
                                </a>
                              </div>

                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="w-4 h-4 text-secondary" />
                                <a href={`tel:${lead.telefono}`} className="text-secondary hover:underline">
                                  {lead.telefono}
                                </a>
                              </div>

                              <div className="flex items-center gap-2 text-sm">
                                <Globe className="w-4 h-4 text-[#6f9533]" />
                                <a href={lead.url} target="_blank" rel="noopener noreferrer" className="text-[#6f9533] hover:underline truncate">
                                  {lead.url}
                                </a>
                              </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-3">
                              <div className="flex flex-wrap gap-2">
                                <span className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-semibold">
                                  Plan: {lead.plan}
                                </span>
                                <span className="inline-block px-3 py-1 rounded-full bg-destructive/10 border border-destructive/30 text-destructive text-xs font-semibold">
                                  {lead.urgencia}
                                </span>
                                <span className="inline-block px-3 py-1 rounded-full bg-secondary/10 border border-secondary/30 text-secondary text-xs font-semibold">
                                  {lead.canal}
                                </span>
                              </div>

                              <div>
                                <p className="text-sm font-medium text-foreground/70 mb-1">Mensaje:</p>
                                <p className="text-sm text-foreground bg-background/50 rounded p-3 border border-border">
                                  {lead.mensaje}
                                </p>
                              </div>

                              {lead.source && (
                                <div className="text-xs text-foreground/50">
                                  Fuente: {lead.source}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
